import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService, TenantService } from '@hta/auth';
import { from, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

/**
 * Guard que verifica si el usuario autenticado tiene al menos un corporativo (tenant)
 * y que puede acceder a él.
 *
 * Flujo (backend como fuente de verdad):
 * 1. Consulta GET /api/auth/tenants (TenantFree, no requiere cookie):
 *    - Sin tenants en BD → /tenant-selection (ahí se verifican invitaciones pendientes)
 * 2. Lee custom:currentTenant de Cognito:
 *    - No seleccionado o no existe en la lista → /tenant-selection
 * 3. Todo OK → permite la navegación
 *
 * IMPORTANTE: siempre redirigimos a /tenant-selection y NO a /create-tenant,
 * porque tenant-selection verifica invitaciones pendientes primero.
 * Si no hay invitaciones ni tenants, tenant-selection redirige a /create-tenant.
 */
export const TenantGuard: CanActivateFn = (_route, _state) => {
    const router        = inject(Router);
    const authService   = inject(AuthService);
    const tenantService = inject(TenantService);

    // Paso 1: consultar tenants reales del backend (no requiere cookie)
    return from(tenantService.getTenants()).pipe(
        switchMap((tenants) => {
            // Sin tenants en BD → ir a tenant-selection (verificará invitaciones pendientes)
            if (!tenants || tenants.length === 0) {
                return of(router.parseUrl('/tenant-selection'));
            }

            // Paso 2: leer currentTenant de Cognito
            return from(authService.getTenantAttributes()).pipe(
                map(({ currentTenant }) => {
                    // Verificar que el currentTenant seleccionado existe entre los tenants reales
                    if (currentTenant !== null && tenants.some(t => t.tenantId === currentTenant)) {
                        return true as const;
                    }
                    // currentTenant no seleccionado o no válido → elegir uno
                    return router.parseUrl('/tenant-selection');
                }),
                catchError(() => {
                    // Error leyendo Cognito → al menos tiene tenants, que elija uno
                    return of(router.parseUrl('/tenant-selection'));
                })
            );
        }),
        catchError(() => {
            // Error de red/backend → intentar con Cognito como fallback
            return from(authService.getTenantAttributes()).pipe(
                map(({ currentTenant, tenantIds }) => {
                    if (currentTenant !== null && tenantIds && tenantIds.length > 0) {
                        // Cognito dice que tiene tenant → dejar pasar (el cookie interceptor resuelve)
                        return true as const;
                    }
                    return router.parseUrl('/tenant-selection');
                }),
                catchError(() => of(router.parseUrl('/tenant-selection')))
            );
        })
    );
};
